import { createSignal, onMount, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { crypto } from "@boveda/shared";
import { Check, Copy, RefreshCw } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Label } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";

interface PasswordGeneratorProps {
  /** Optional callback: "Use" inserts the generated value into a form. */
  onUse?: (value: string) => void;
}

const DEFAULT_LENGTH = 20;
const MIN_LENGTH = 8;
const MAX_LENGTH = 64;
const DEFAULT_WORDS = 5;
const MIN_WORDS = 3;
const MAX_WORDS = 10;
const COPIED_FEEDBACK_MS = 1500;
// Entropy thresholds (bits) for the strength label.
const ENTROPY_FAIR = 40;
const ENTROPY_STRONG = 60;
const ENTROPY_EXCELLENT = 80;

/** Password/passphrase generator with visible entropy. */
export function PasswordGenerator(props: PasswordGeneratorProps) {
  const { t } = useI18n();
  const [mode, setMode] = createSignal<"password" | "passphrase">("password");
  const [length, setLength] = createSignal(DEFAULT_LENGTH);
  const [words, setWords] = createSignal(DEFAULT_WORDS);
  const [useUpper, setUseUpper] = createSignal(true);
  const [useDigits, setUseDigits] = createSignal(true);
  const [useSymbols, setUseSymbols] = createSignal(true);
  const [value, setValue] = createSignal("");
  const [entropy, setEntropy] = createSignal(0);
  const [copied, setCopied] = createSignal(false);

  function generate(): void {
    const result =
      mode() === "password"
        ? crypto.generatePassword({
            length: length(),
            uppercase: useUpper(),
            digits: useDigits(),
            symbols: useSymbols(),
          })
        : crypto.generatePassphrase({ words: words() });
    setValue(result.value);
    setEntropy(Math.round(result.entropyBits));
    setCopied(false);
  }

  // Generate one on mount.
  onMount(() => {
    generate();
  });

  async function copy(): Promise<void> {
    await navigator.clipboard.writeText(value());
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  }

  const strengthLabel = (): string => {
    const bits = entropy();
    if (bits < ENTROPY_FAIR) {
      return t(msg`Weak`);
    }
    if (bits < ENTROPY_STRONG) {
      return t(msg`Fair`);
    }
    if (bits < ENTROPY_EXCELLENT) {
      return t(msg`Strong`);
    }
    return t(msg`Excellent`);
  };

  return (
    <div class="flex flex-col gap-4 rounded-xl border border-border/60 bg-card/40 p-4">
      {/* Output */}
      <div class="flex items-center gap-2">
        <output
          class="flex-1 break-all rounded-md bg-background px-3 py-2 font-mono text-sm"
          data-testid="generator-output"
        >
          {value()}
        </output>
        <Button variant="ghost" size="icon" aria-label={t(msg`Regenerate`)} onClick={generate}>
          <RefreshCw class="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t(msg`Copy`)}
          onClick={() => void copy()}
          data-testid="generator-copy"
        >
          <Show when={copied()} fallback={<Copy class="h-4 w-4" />}>
            <Check class="h-4 w-4 text-success" />
          </Show>
        </Button>
      </div>

      <p class="text-xs text-muted-foreground">
        {strengthLabel()} · {entropy()} {t(msg`bits of entropy`)}
      </p>

      {/* Mode */}
      <div class="flex gap-2">
        <Button
          size="sm"
          variant={mode() === "password" ? "default" : "outline"}
          onClick={() => {
            setMode("password");
            generate();
          }}
        >
          {t(msg`Password`)}
        </Button>
        <Button
          size="sm"
          variant={mode() === "passphrase" ? "default" : "outline"}
          onClick={() => {
            setMode("passphrase");
            generate();
          }}
        >
          {t(msg`Passphrase`)}
        </Button>
      </div>

      {/* Options */}
      <Show
        when={mode() === "password"}
        fallback={
          <label class="flex items-center justify-between text-sm">
            <span>
              {t(msg`Words`)}: {words()}
            </span>
            <input
              type="range"
              min={MIN_WORDS}
              max={MAX_WORDS}
              value={words()}
              onInput={(e) => {
                setWords(Number(e.currentTarget.value));
                generate();
              }}
            />
          </label>
        }
      >
        <div class="flex flex-col gap-3">
          <label class="flex items-center justify-between text-sm">
            <span>
              {t(msg`Length`)}: {length()}
            </span>
            <input
              type="range"
              min={MIN_LENGTH}
              max={MAX_LENGTH}
              value={length()}
              onInput={(e) => {
                setLength(Number(e.currentTarget.value));
                generate();
              }}
            />
          </label>
          <div class="flex flex-wrap gap-4 text-sm">
            <Label class="flex items-center gap-2">
              <input
                type="checkbox"
                checked={useUpper()}
                onChange={(e) => {
                  setUseUpper(e.currentTarget.checked);
                  generate();
                }}
              />
              {t(msg`Uppercase`)}
            </Label>
            <Label class="flex items-center gap-2">
              <input
                type="checkbox"
                checked={useDigits()}
                onChange={(e) => {
                  setUseDigits(e.currentTarget.checked);
                  generate();
                }}
              />
              {t(msg`Digits`)}
            </Label>
            <Label class="flex items-center gap-2">
              <input
                type="checkbox"
                checked={useSymbols()}
                onChange={(e) => {
                  setUseSymbols(e.currentTarget.checked);
                  generate();
                }}
              />
              {t(msg`Symbols`)}
            </Label>
          </div>
        </div>
      </Show>

      <Show when={props.onUse}>
        <Button
          variant="secondary"
          onClick={() => props.onUse?.(value())}
          data-testid="generator-use"
        >
          {t(msg`Use this password`)}
        </Button>
      </Show>
    </div>
  );
}
